import { useCallback } from 'react'
import { useStartImpersonationMutation } from '../../__generated__/graphql'
import { ENVIRON } from '../../env'
import { useAppTranslations } from './useAppTranslations'
import { PWS_COMPANY_ID } from './useUserContext'
import { useSnackbarManager } from '../providers/snackbarContext'
import { getUserToken, isImpersonating, quitImpersonationSession, startImpersonationSession } from '../tokenManagement'

interface ImpersonationTarget {
  id: string | number
  firstName?: string | null
  lastName?: string | null
  status?: string | null
  companyId?: string | number | null
}

export const useImpersonation = (): {
  isImpersonating: boolean
  startImpersonation: (target: ImpersonationTarget) => Promise<void>
  quitImpersonation: () => void
} => {
  const t = useAppTranslations()
  const [, setSnackbar] = useSnackbarManager()
  const [startImpersonationMutation] = useStartImpersonationMutation()

  const startImpersonation = useCallback(
    async (target: ImpersonationTarget): Promise<void> => {
      const originalToken = getUserToken()
      if (!originalToken) {
        setSnackbar({ severity: 'error', message: t('impersonation.errors.notAuthenticated') })
        return
      }

      try {
        const result = await startImpersonationMutation({ variables: { userId: String(target.id) } })
        const impersonationResult = result.data?.startImpersonation
        if (!impersonationResult?.token) throw new Error(t('impersonation.errors.startFailed'))

        startImpersonationSession(originalToken, impersonationResult.token)
        window.location.assign('/home')
      } catch (error) {
        setSnackbar({
          severity: 'error',
          message: error instanceof Error ? error.message : t('impersonation.errors.startFailed'),
        })
      }
    },
    [setSnackbar, startImpersonationMutation, t]
  )

  const quitImpersonation = useCallback((): void => {
    quitImpersonationSession()
    window.location.assign('/home')
  }, [])

  return {
    isImpersonating: isImpersonating(),
    startImpersonation,
    quitImpersonation,
  }
}

export const canImpersonateUser = (target: ImpersonationTarget, currentUserId: string | number): boolean => {
  if (String(target.id) === String(currentUserId)) return false
  if (target.status && target.status !== 'Enabled') return false
  if (ENVIRON !== 'development' && String(target.companyId) === PWS_COMPANY_ID) return false

  return true
}
